import { Plus } from "lucide-react";
import type { CSSProperties, ReactNode } from "react";
import ButtonV2 from "../../../../../../src/components/ui/button/v2";
import { OptionNameValuePair } from "../../../../../../src/lib/interfaces/types";
import { cn } from "../../../../../lib/utils";
import EmptyTableIcon from "../../../../../assets/svg/EmptyTableIcon";
import BaseDataTable, {
  ColumnType,
  IColumnConfig,
  IDataRow,
  TextCellProps,
} from "../../../../../components/ui/baseDataTable";

export interface FMRTableColumn<DataType, FieldType = any> {
  headerName: string;
  headerRender?: ReactNode;
  headerClassName?: string;
  headerStyle?: CSSProperties;
  type: ColumnType;
  field: keyof DataType;
  isEditable: NonNullable<IColumnConfig<DataType, FieldType>["isEditable"]>;
  options?: OptionNameValuePair<FieldType>[];
  className?: string;
  textCellProps?: Partial<TextCellProps>;
  getValue: (data: DataType) => FieldType;
  onChange?: (rowIndex: number, value: FieldType, field: keyof DataType) => void;
  tableCellClassName?: string;
  tableCellStyle?: CSSProperties;
}

export interface FMRTableProps<DataType> {
  columns: FMRTableColumn<DataType>[];
  rows: DataType[];
  onAdd: (rowIndex: number) => void;
  onDelete: (rowIndex: number) => void;
  title?: string;
  emptyStateContainerClassName?: string;
  rounded?: boolean;
  disableFocusWithinStyles?: boolean;
  disableActiveCellStyles?: boolean;
}

export const FMRTable = <DataType,>(props: FMRTableProps<DataType>) => {
  const {
    columns,
    rows,
    onAdd,
    onDelete,
    title,
    emptyStateContainerClassName,
    rounded,
    disableFocusWithinStyles,
    disableActiveCellStyles,
  } = props;

  const renderHeaderWithRequiredAsterisks = (headerName: string): ReactNode => {
    if (!headerName.includes("**")) return headerName;
    const parts = headerName.split("**");
    return (
      <span className="inline-flex items-center align-middle">
        {parts.map((part, idx) => (
          <span key={idx} className="inline-flex items-center">
            {part}
            {idx < parts.length - 1 ? <span className="text-[#DC2626] font-medium">**</span> : null}
          </span>
        ))}
      </span>
    );
  };

  /* ---------------- Default Empty State ---------------- */

  const renderDefaultTableBody = () => (
    <div className={emptyStateContainerClassName ?? "w-[100vw]"}>
      <div className="flex flex-col items-center justify-center py-8 px-4">
        <EmptyTableIcon />

        <span className="text-[#0A0A0A] font-sans font-semibold text-base leading-[1.5rem] text-center mt-4 mb-2 max-w-full">
          No Data Added
        </span>

        <span className="text-[#737373] font-sans font-normal text-sm leading-[1.25rem] text-center mb-4 max-w-md">
          No data added for this section.
        </span>

        <ButtonV2 onClick={() => onAdd(0)}>
          <Plus className="w-4 h-4 mr-1" /> Add
        </ButtonV2>
      </div>
    </div>
  );

  const columnConfigs: IColumnConfig<DataType>[] = columns.map((col) => {
    const baseConfig: IColumnConfig<DataType> = {
      headerName: col.headerName,
      headerRender: col.headerRender ?? renderHeaderWithRequiredAsterisks(col.headerName),
      headerClassName: cn(
        "px-2 py-2 h-14 min-h-[56px] align-middle whitespace-normal",
        col.headerClassName,
      ),
      headerStyle: col.headerStyle,
      type: col.type,
      field: col.field,
      getValue: col.getValue,
      isEditable: col.isEditable,
      onChange: col.onChange,
      tableCellClassName: col.tableCellClassName,
      tableCellStyle: col.tableCellStyle,
    };

    if (typeof baseConfig.isEditable === "boolean") {
      baseConfig.hideFocus = !baseConfig.isEditable;
    }

    if (col.type === "select" || col.type === "multiselect") {
      baseConfig.options = col.options;
    }

    if (col.type === "text") {
      const mergedTextCellProps: TextCellProps = {
        className:
          col.className ??
          col.textCellProps?.className ??
          "px-0 min-w-[130px] overflow-hidden text-ellipsis whitespace-nowrap text-[#0A0A0A] font-sans text-sm font-normal leading-5",
        textareaClassName:
          col.textCellProps?.textareaClassName ??
          "px-0 text-sm min-w-[130px] text-[#0A0A0A] font-sans font-normal leading-5 ",
        numberOfRows: col.textCellProps?.numberOfRows ?? 1,
        ...col.textCellProps,
      };

      baseConfig.textCellProps = {
        ...mergedTextCellProps,
      };
    }

    if (col.type === "select") {
      baseConfig.selectCellProps = {
        className: col.className ?? "",
        rootContainerClassName: "flex items-center min-w-[120px]",
        placeholder: "",
        dropdownClassName: "w-max min-w-full [&>div:first-child]:hidden",
        labelClassName: "px-0",
      };
    }

    if (col.type === "number") {
      baseConfig.numberCellProps = {
        className: col.className ?? "px-0 min-w-[80px]",
      };
    }

    if (col.type === "date") {
      baseConfig.dateCellProps = {
        className: col.className ?? "px-0 min-w-[110px]",
        inputClassName: "bg-transparent",
      };
    }

    return baseConfig;
  });

  const dataRows: IDataRow<DataType>[] = rows.map((row, index) => ({
    data: row,
    isEditable: true,
    widgets: ["add", "delete"],
    onAdd: () => onAdd(index),
    onDelete: () => onDelete(index),
  }));

  return (
    <div className="flex flex-col gap-2">
      {title && <span className="font-sans text-xs font-medium leading-none text-[#6B7280]">{title}</span>}

      <div className="w-full min-w-0 overflow-x-auto overflow-y-hidden [&_tbody>tr>td:not(:first-child)]:!px-4 [&_tbody>tr>td:not(:first-child)]:!py-2">
        <BaseDataTable<DataType>
          columnConfigs={columnConfigs}
          dataRows={dataRows}
          showDefaultTableBody={rows.length === 0}
          defaultTableBody={renderDefaultTableBody()}
          rounded={rounded}
          disableFocusWithinStyles={disableFocusWithinStyles}
          disableActiveCellStyles={disableActiveCellStyles}
        />
      </div>
    </div>
  );
};
